import { getWeek } from '@/lib/date-utils'
import Td2 from '../../../components/zthd/Td2'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'

export const columnDate = {
  id: 'date',
  accessorFn: (row) => row.date,
  header: 'Date',
  cell: (row) => {
    const date = row.getValue()
    const [, week] = getWeek(date)
    return (
      <Td2 line2={<div className="flex gap-1 justify-end">周{week}</div>}>
        {date}
      </Td2>
    )
  },
  enableHiding: false,
  filterFn: (row, columnId, filterValue) => {
    const date = row.getValue(columnId)
    const [weekIndex] = getWeek(date)
    return weekIndex === Number(filterValue)
  },
  meta: {
    sticky: true,
    stickyLeft: 'left-[0px]',
  },
}

export const costColumn = (code) => ({
  id: `${code}-costs`,
  header: `Costs`,
  accessorFn: (row) => {
    const curr = row.costs?.[code]
    const total = row.costs?.total
    return { curr, total }
  },
  cell: (row) => {
    const { curr, total } = row.getValue()
    const formatted = formatUSD(curr?.costs, 0, 0)
    const per = formatPercent(curr?.costs / total?.costs)
    return <Td2 line2={per}>{formatted}</Td2>
  },
})

export const installColumn = (code) => ({
  id: `${code}-installs`,
  header: 'Installs',
  accessorFn: (row) => {
    const curr = row.costs?.[code]
    const total = row.datas?.total
    return { curr, total }
  },
  cell: (row) => {
    const { curr, total } = row.getValue()
    const formatted = formatNumber(curr?.installs, 0, 0)
    const per = formatPercent(curr?.installs / total?.installs)
    return <Td2 line2={per}>{formatted}</Td2>
  },
})

export const cpiColumn = (code) => ({
  id: `${code}-cpi`,
  header: 'CPI',
  accessorFn: (row) => {
    const curr = row.costs?.[code]
    return curr?.costs / curr?.installs
  },
  cell: (row) => {
    const cpi = row.getValue()
    const formatted = formatUSD(cpi, 4, 4)
    return <Td2 line2="-">{formatted}</Td2>
  },
})

export const costGroupColumn = (code, name, isGroup = false) => ({
  id: `${code}-cost-group`,
  header: isGroup ? `Costs` : `${code}-${name}`,
  columns: isGroup
    ? [costColumn(code), cpiColumn(code)]
    : [costColumn(code), installColumn(code), cpiColumn(code)],
})

export const revenueColumn = (code, name, isGroup = false) => ({
  id: `${code}-revenue`,
  header: isGroup ? `Revenue` : `${code}-${name}`,
  accessorFn: (row) => {
    const curr = row.revenues?.[code]
    const total = row.revenues?.total
    return { curr, total }
  },
  cell: (row) => {
    const { curr, total } = row.getValue()
    const formatted = formatUSD(curr, 0, 0)
    const per = formatPercent(curr / total)
    return <Td2 line2={per}>{formatted}</Td2>
  },
})

export const profitColumn = (code, name, isGroup = false) => ({
  id: `${code}-profit`,
  header: isGroup ? `Profit` : `${code}-${name}`,
  accessorFn: (row) => {
    const costs = row.costs?.[code]?.costs
    const revenues = row.revenues?.[code]
    const profit = revenues - costs

    const costsTotal = row.costs?.total?.costs
    const revenuesTotal = row.revenues?.total

    const profitTotal = revenuesTotal - costsTotal

    return { curr: profit, total: profitTotal }
  },
  cell: (row) => {
    const { curr, total } = row.getValue()
    const formatted = formatUSD(curr, 0, 0)
    const per = formatPercent(curr / total)
    return <Td2 line2={per}>{formatted}</Td2>
  },
})

const erColumn = (code, name) => ({
  id: `${code}-er`,
  header: `ER`,
  accessorFn: (row) => {
    const costs = row.costs?.[code]?.costs
    const revenues = row.revenues?.[code]
    const profit = revenues - costs

    return { pr: profit / revenues }
  },
  cell: (row) => {
    const { pr } = row.getValue()
    const formatted = formatPercent(pr)
    return <Td2 line2="-">{formatted}</Td2>
  },
})

const roasColumn = (code, name) => ({
  id: `${code}-roas`,
  header: `ROAS`,
  accessorFn: (row) => {
    return { curr: row.drs?.[code] }
  },
  cell: (row) => {
    const { curr } = row.getValue()
    const formatted = formatPercent(curr)
    return <Td2 line2="-">{formatted}</Td2>
  },
})

const spaceColumn = (code) => ({
  id: `${code}-space`,
  header: () => <div className="w-2"></div>,
  cell: () => <div className="w-2"></div>,
})

export const summaryGroupColumn = (code, name, isLight) => ({
  id: `${code}`,
  header: `${code}-${name}`,
  columns: [
    revenueColumn(code, name, true),
    costColumn(code),
    profitColumn(code, name, true),
    erColumn(code, name),
    roasColumn(code, name),
    cpiColumn(code, name),
  ],
  meta: { isLight },
})